import { useEffect, useCallback, useRef } from "react";

/**
 * useKeyboardAware - Hook para lidar com teclado virtual em tablets
 * 
 * Funcionalidades:
 * - Detecta quando o teclado virtual abre/fecha
 * - Rola automaticamente o input focado para a área visível
 * - Ajusta a viewport para não sobrepor conteúdo
 */
export function useKeyboardAware(options = {}) {
  const { 
    enabled = true,
    scrollOffset = 150, // Espaço extra acima do input
    animationDuration = 300 
  } = options;

  const lastFocusedElement = useRef(null);
  const initialViewportHeight = useRef(typeof window !== 'undefined' ? window.innerHeight : 0);

  // Detectar se é um dispositivo touch (tablet/mobile)
  const isTouchDevice = useCallback(() => {
    if (typeof window === 'undefined') return false;
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }, []);

  // Rolar o elemento focado para a área visível
  const scrollToFocusedElement = useCallback((element) => {
    if (!element || !enabled) return;

    // Aguardar o teclado abrir completamente
    setTimeout(() => {
      const rect = element.getBoundingClientRect();
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      
      // Se o elemento está abaixo da área visível
      if (rect.bottom > viewportHeight - scrollOffset) {
        element.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center',
          inline: 'nearest'
        });
      }
    }, animationDuration);
  }, [enabled, scrollOffset, animationDuration]);

  // Handler para focus em inputs
  const handleFocus = useCallback((event) => {
    const target = event.target;
    const tagName = target.tagName?.toLowerCase();
    
    // Verificar se é um elemento de input
    if (tagName === 'input' || tagName === 'textarea' || tagName === 'select') {
      lastFocusedElement.current = target;
      scrollToFocusedElement(target);
    }
  }, [scrollToFocusedElement]);

  // Handler para resize da viewport (teclado abrindo/fechando)
  const handleResize = useCallback(() => {
    if (!enabled || !isTouchDevice()) return;

    const currentHeight = window.visualViewport?.height || window.innerHeight;
    const isKeyboardOpen = currentHeight < initialViewportHeight.current * 0.75;

    if (isKeyboardOpen && lastFocusedElement.current) {
      scrollToFocusedElement(lastFocusedElement.current);
    }
  }, [enabled, isTouchDevice, scrollToFocusedElement]);

  // Setup event listeners
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;

    // Usar Visual Viewport API se disponível (melhor suporte a teclado virtual)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    } else {
      window.addEventListener('resize', handleResize);
    }

    // Listener global para focus em inputs
    document.addEventListener('focusin', handleFocus, true);

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
      } else {
        window.removeEventListener('resize', handleResize);
      }
      document.removeEventListener('focusin', handleFocus, true);
    };
  }, [enabled, handleResize, handleFocus]);

  // Função para forçar scroll manual
  const scrollInputIntoView = useCallback((inputElement) => {
    if (inputElement) {
      scrollToFocusedElement(inputElement);
    }
  }, [scrollToFocusedElement]);

  return {
    isTouchDevice: isTouchDevice(),
    scrollInputIntoView
  };
}
